// ../../store/authStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiService } from '../services/api';
import { setTokens, clearTokens, getAccessToken, bootstrapAuth } from '../services/httpClient';

/**
 * Decode the `exp` claim from a JWT and return true if the token is past its
 * expiration (or unparseable). We intentionally do NOT verify the signature
 * here — the backend is the source of truth — but checking `exp` client-side
 * lets us prune dead sessions before issuing a doomed API call.
 */
export function isJwtExpired(token: string | null | undefined): boolean {
  if (!token) return true;
  // Sprint 94 (finding 94.5) — accept the mock sentinel ONLY outside
  // production. Gating on `!== 'production'` (rather than `=== 'development'`)
  // keeps it available under the test runner while guaranteeing a production
  // build can never admit a mock token: in prod this branch is skipped and
  // 'mock-jwt-token' falls through to the decode path, which fails (not three
  // dot-separated segments) and is treated as expired.
  if (process.env.NODE_ENV !== 'production' && token === 'mock-jwt-token') {
    return false;
  }
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    // base64url → base64
    const payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payloadB64 + '='.repeat((4 - (payloadB64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    if (typeof payload.exp !== 'number') return true;
    // exp is seconds since epoch; 30s skew tolerance.
    return payload.exp * 1000 < Date.now() - 30_000;
  } catch {
    return true;
  }
}


// Types
export interface User {
  id: string;
  email: string;
  name?: string;
  role: 'owner' | 'admin' | 'super_admin_condo' | 'board-member';
  condoId?: string;
  ownerId?: string;
  permissions: string[];
  isActive: boolean;
  avatar?: string;
  phone?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Condo {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  totalUnits: number;
 /// occupiedUnits: number;
  monthlyFee: number;
  isActive: boolean;
  contactEmail?: string;
  contactPhone?: string;
  managedSince?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface FeatureVisibility {
  adminVisible: boolean;
  ownerVisible: boolean;
  boardMemberVisible: boolean;
}

export type FeatureSettings = Record<string, FeatureVisibility>;

// Default feature settings — controls per-role visibility of each route.
// Keys must match the route keys used in MainDashboard.tsx allMenuItems.
export const DEFAULT_FEATURE_SETTINGS: FeatureSettings = {
  // ── Overview ──────────────────────────────────────────────────────────────
  'overview':              { adminVisible: true,  ownerVisible: true,  boardMemberVisible: true  },
  'tutorial':              { adminVisible: true,  ownerVisible: true,  boardMemberVisible: true  },
  'board-member-portal':   { adminVisible: true,  ownerVisible: false, boardMemberVisible: true  },
  // ── Property ──────────────────────────────────────────────────────────────
  'condos':                { adminVisible: true,  ownerVisible: false, boardMemberVisible: false },
  'units':                 { adminVisible: true,  ownerVisible: false, boardMemberVisible: false },
  // ── Residents ─────────────────────────────────────────────────────────────
  'owners':                { adminVisible: true,  ownerVisible: false, boardMemberVisible: true  },
  'violations':            { adminVisible: true,  ownerVisible: false, boardMemberVisible: true  },
  'board':                 { adminVisible: true,  ownerVisible: false, boardMemberVisible: true  },
  'portal':                { adminVisible: true,  ownerVisible: true,  boardMemberVisible: false },
  'status-certificates':   { adminVisible: true,  ownerVisible: false, boardMemberVisible: false },
  'agm-packets':           { adminVisible: true,  ownerVisible: false, boardMemberVisible: true  },
  // ── My Home (owner) ───────────────────────────────────────────────────────
  'my-home':               { adminVisible: false, ownerVisible: true,  boardMemberVisible: false },
  // ── Finances ──────────────────────────────────────────────────────────────
  'fee-management':        { adminVisible: true,  ownerVisible: false, boardMemberVisible: false },
  'financial-management':  { adminVisible: true,  ownerVisible: false, boardMemberVisible: true  },
  'statements':            { adminVisible: true,  ownerVisible: true,  boardMemberVisible: false },
  'payments':              { adminVisible: true,  ownerVisible: true,  boardMemberVisible: false },
  'reports-analytics':     { adminVisible: true,  ownerVisible: false, boardMemberVisible: true  },
  // ── Operations ────────────────────────────────────────────────────────────
  'maintenance-hub':       { adminVisible: true,  ownerVisible: true,  boardMemberVisible: true  },
  'admin-operations':      { adminVisible: true,  ownerVisible: false, boardMemberVisible: true  },
  'property-operations':   { adminVisible: true,  ownerVisible: true,  boardMemberVisible: false },
  'amenities':             { adminVisible: true,  ownerVisible: true,  boardMemberVisible: true  },
  'communications':        { adminVisible: true,  ownerVisible: true,  boardMemberVisible: true  },
  'documents':             { adminVisible: true,  ownerVisible: true,  boardMemberVisible: true  },
  // ── System ────────────────────────────────────────────────────────────────
  'feature-permissions':   { adminVisible: false, ownerVisible: false, boardMemberVisible: false },
};

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  currentCondo: Condo | null;
  availableCondos: Condo[];
  featureSettings: FeatureSettings;

  // Auth Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  resetError: () => void;
  checkAuth: () => Promise<void>;
  updateUserProfile: (userData: Partial<User>) => Promise<void>;
  // Sprint 123.2 — owner profile SELF-edit against the real backend endpoint
  // (PUT /api/auth/user/{id}). Strictly self-scoped: always sends the logged-in
  // user's own id. Merges the server response into local state and rethrows on
  // failure so the caller can toast the message.
  updateMyProfile: (fields: { name?: string; email?: string; phoneNumber?: string }) => Promise<void>;

  // Feature Settings Actions
  setFeatureSettings: (settings: FeatureSettings) => void;
  updateFeatureSetting: (featureKey: string, visibility: Partial<FeatureVisibility>) => void;
  resetFeatureSettings: () => void;

  // Condo Actions
  switchCondo: (condoId: string) => void;
  fetchAvailableCondos: () => Promise<void>;
  setCurrentCondo: (condo: Condo | null) => void;
}

// Mock data for development
const mockUsers: User[] = [
  {
    id: '1',
    email: 'owner@example.com',
    name: 'John Doe',
    role: 'owner',
    condoId: '1',
    ownerId: 'owner-001',
    permissions: ['view_dashboard', 'view_statements', 'make_payments', 'view_documents'],
    isActive: true,
    phone: '+1 (555) 123-4567',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=John'
  },
  {
    id: '2',
    email: 'admin@example.com',
    name: 'Jane Smith',
    role: 'admin',
    condoId: '1',
    permissions: ['view_dashboard', 'manage_owners', 'manage_fees', 'view_reports', 'manage_documents'],
    isActive: true,
    phone: '+1 (555) 987-6543',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Jane'
  },
  {
    id: '3',
    email: 'super@admin.com',
    name: 'Super Admin',
    role: 'super_admin_condo',
    permissions: ['all'],
    isActive: true,
    phone: '+1 (555) 111-2222',
    avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Super'
  }
];

const mockCondos: Condo[] = [
  {
    id: '3e4f5a6b-7c8d-9e0f-1a2b-3c4d5e6f7a8b',
    name: 'Ocean View Towers',
    address: '123 Beachfront Ave',
    city: 'Miami',
    state: 'FL',
    zipCode: '33139',
    totalUnits: 50,
  //  occupiedUnits: 45,
    monthlyFee: 350,
    isActive: true,
    contactEmail: 'manager@oceanview.com',
    contactPhone: '+1 (555) 123-4567',
    managedSince: '2020-01-01'
  },
  {
    id: '2',
    name: 'Mountain Peak Residences',
    address: '456 Highland Rd',
    city: 'Denver',
    state: 'CO',
    zipCode: '80202',
    totalUnits: 30,
   // occupiedUnits: 28,
    monthlyFee: 420,
    isActive: true,
    contactEmail: 'admin@mountainpeak.com',
    contactPhone: '+1 (555) 987-6543',
    managedSince: '2021-03-15'
  },
  {
    id: '3',
    name: 'City Center Apartments',
    address: '789 Downtown St',
    city: 'New York',
    state: 'NY',
    zipCode: '10001',
    totalUnits: 75,
 //   occupiedUnits: 70,
    monthlyFee: 550,
    isActive: true,
    contactEmail: 'info@citycenter.com',
    contactPhone: '+1 (555) 456-7890',
    managedSince: '2019-06-01'
  }
];

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial State
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      currentCondo: null,
      availableCondos: [],
      featureSettings: DEFAULT_FEATURE_SETTINGS,

      // Login Action
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        
        try {
          // Check if we should use API or mock data.
          // Sprint 94 (finding 94.5) — the mock-login path writes a
          // 'mock-jwt-token' sentinel to localStorage; it must NEVER run in a
          // production build. The `&& false` keeps it off by default (flip the
          // literal locally to exercise the mock flow), while the
          // `NODE_ENV !== 'production'` guard guarantees a prod bundle can
          // never take the mock-auth branch regardless of that literal.
          const useMockData = process.env.NODE_ENV !== 'production' && false;

          if (useMockData) {
            // Use mock data for development
            await new Promise(resolve => setTimeout(resolve, 800)); // Simulate API delay
            
            const mockUser = mockUsers.find(u => 
              u.email.toLowerCase() === email.toLowerCase() && 
              password === 'password123' // Simple mock password for all users
            );
            
            if (!mockUser) {
              throw new Error('Invalid email or password');
            }
            
            // Determine available condos based on user role
            let availableCondos: Condo[] = [];
            if (mockUser.role === 'super_admin_condo') {
              availableCondos = mockCondos;
            } else if (mockUser.condoId) {
              availableCondos = mockCondos.filter(c => c.id === mockUser.condoId);
            }
            
            // Set current condo
            let currentCondo: Condo | null = null;
            if (mockUser.condoId && availableCondos.length > 0) {
              currentCondo = availableCondos[0];
            } else if (availableCondos.length > 0) {
              currentCondo = availableCondos[0];
            }
            
            set({
              user: mockUser,
              isAuthenticated: true,
              availableCondos,
              currentCondo,
              isLoading: false,
              error: null
            });
            
            // Sprint 110 (110.1) — the access token lives in memory only,
            // never localStorage. The mock path seeds the in-memory store via
            // setTokens (dev-only sentinel; gated off in prod by useMockData).
            setTokens('mock-jwt-token', null);

          } else {
            // Use real API in production
            try {
              // Sprint 2: backend now returns refreshToken alongside the
              // short-lived access token. setTokens() stores both — the
              // httpClient picks them up automatically on every request and
              // performs single-flight refresh on 401.
              const { token, refreshToken, user: rawUser } =
                await apiService.login(email, password);
              setTokens(token, refreshToken ?? null);

              // Normalize role: backend returns uppercase enum names (e.g. "ADMIN", "SUPER_ADMIN_CONDO")
              // but frontend role gates expect lowercase strings ("admin", "super_admin_condo").
              const ROLE_MAP: Record<string, string> = {
                ADMIN:            'admin',
                OWNER:            'owner',
                SUPER_ADMIN_CONDO:'super_admin_condo',
                MANAGER:          'board-member',
                STAFF:            'admin',
                ALL:              'super_admin_condo',
                SUPER_ADMIN_MED:  'super_admin_condo',
              };
              const user = {
                ...rawUser,
                role: ROLE_MAP[String(rawUser.role).toUpperCase()] ?? String(rawUser.role).toLowerCase(),
              };

              // Fetch available condos — non-fatal: dashboard will retry via fetchAvailableCondos
              // /condos/available is scoped by the backend: super_admin gets all, admin/owner gets their own
              let availableCondos: Condo[] = [];
              try {
                availableCondos = await apiService.getCondos();
              } catch {
                // Condos will be loaded by the dashboard on mount
              }

              // Sprint 31 — finding R10 (round-3 e2e). If /condos/available
              // failed (e.g. the LazyInitializationException R6 used to
              // produce, or any transient 500), fall back to the condo
              // attached to the login response. Without this fallback the
              // owner landed on the dashboard with availableCondos=[] and
              // currentCondo=null even though their condo binding was right
              // there in the user payload — which surfaced as
              // "Select Property / No owner data found" on My Home.
              const userCondoFromLogin = (user as any).condo as Condo | undefined;
              if (availableCondos.length === 0 && userCondoFromLogin?.id) {
                availableCondos = [userCondoFromLogin];
              }

              // Set current condo. R11-9 — prefer the last condo this browser
              // worked in (persisted by switchCondo/setCurrentCondo under a key
              // that survives logout's auth-storage wipe), so a super-admin's
              // context no longer resets to the first org on every re-login.
              // Then fall back to user.condoId, then first available.
              const lastCondoId = (() => {
                try { return localStorage.getItem('last-condo-id'); } catch { return null; }
              })();
              let currentCondo: Condo | null = null;
              if (lastCondoId && availableCondos.some(c => c.id === lastCondoId)) {
                currentCondo = availableCondos.find(c => c.id === lastCondoId) || null;
              } else if (user.condoId && availableCondos.length > 0) {
                currentCondo = availableCondos.find(c => c.id === user.condoId) || availableCondos[0];
              } else if (availableCondos.length > 0) {
                currentCondo = availableCondos[0];
              }

              set({
                user,
                isAuthenticated: true,
                availableCondos,
                currentCondo,
                isLoading: false,
                error: null
              });
            } catch (apiError: any) {
              throw new Error(apiError.message || 'API login failed');
            }
          }
          
        } catch (error: any) {
          // Sprint 35 — finding G4. Was a `console.log("Condo logon api
          // tracker failure 3")` leftover from Sprint-6c debugging; it
          // fired on the happy-path catch (every cancelled / 401 login
          // flushes through here) and polluted dev consoles. Silently
          // handle the failure; the surfaced `error` field is what the
          // UI consumes.
          // S6.7: Clear persisted session so stale auth cannot be rehydrated after a failed login
          clearTokens();
          localStorage.removeItem('auth-storage');
          set({
            error: error.message || 'Login failed. Please try again.',
            isLoading: false,
            user: null,
            isAuthenticated: false
          });
          throw error;
        }
      },

      // Logout Action
      logout: () => {
        // Clear access + refresh tokens. The httpClient also clears them when
        // the silent-refresh path itself 401s — but explicit logout should be
        // the same clean state.
        clearTokens();
        // The localStorage removal MUST run AFTER set(): zustand's persist
        // middleware writes back to 'auth-storage' synchronously when set()
        // mutates the store, so removing it before set() lets persist
        // immediately rehydrate the key with the cleared state — the user
        // sees an empty profile in storage, not nothing. Order:
        //   1. set() the empty state         → persist re-writes auth-storage
        //   2. removeItem('auth-storage')    → final clean wipe
        set({
          user: null,
          isAuthenticated: false,
          error: null,
          currentCondo: null,
          availableCondos: []
        });
        localStorage.removeItem('auth-storage');
        localStorage.removeItem('unified_auth_current_app');
      },

      // Reset Error Action
      resetError: () => set({ error: null }),

      // Check Authentication Status
      checkAuth: async () => {
        // Sprint 110 (110.1) — the access token is in memory only and is empty
        // on a fresh page load. Re-hydrate it from the HttpOnly refresh cookie
        // via the silent bootstrap before deciding session state. If there is
        // no valid refresh cookie, bootstrap returns null → genuinely logged
        // out. (In the dev/mock path setTokens already seeded the in-memory
        // token, so getAccessToken() short-circuits the bootstrap below.)
        let token = getAccessToken();
        if (!token) {
          token = await bootstrapAuth();
        }
        if (!token) {
          clearTokens();
          localStorage.removeItem('auth-storage');
          set({
            user: null,
            isAuthenticated: false,
            currentCondo: null,
            availableCondos: []
          });
          return;
        }

        // Prune obviously-dead sessions before we do any work. Saves a round
        // trip and prevents the UI from briefly rendering "logged in" with
        // an expired token. (A token just minted by bootstrap is fresh, so this
        // only bites a stale in-memory token.)
        if (isJwtExpired(token)) {
          clearTokens();
          localStorage.removeItem('auth-storage');
          set({
            user: null,
            isAuthenticated: false,
            currentCondo: null,
            availableCondos: [],
            isLoading: false,
          });
          return;
        }

        set({ isLoading: true });
        
        try {
          // In development, restore from persisted state
          if (process.env.NODE_ENV === 'development') {
            // Get persisted state from localStorage
            const persistedState = localStorage.getItem('auth-storage');
            if (persistedState) {
              const parsed = JSON.parse(persistedState);
              const { user, currentCondo, availableCondos } = parsed.state;

              if (user) {
                set({
                  user,
                  isAuthenticated: true,
                  currentCondo,
                  availableCondos: availableCondos || [],
                  isLoading: false
                });
                return;
              }
            }
            // No persisted user found — we hold a token but no session state.
            // Previously this branch fell through without resetting isLoading,
            // leaving ProtectedRoute spinning forever. Clear the flag so the
            // route guard can redirect to login instead of hanging.
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
            return;
          } else {
            // In production, verify token with API
            try {
              const user = await apiService.getCurrentUser();
              
              // Fetch available condos — /condos/available is scoped per role by backend
              const availableCondos = await apiService.getCondos();

              // Set current condo: prefer stored condo if still valid, else match condoId, else first
              const { currentCondo: storedCondo } = get();
              let currentCondo = storedCondo && availableCondos.some(c => c.id === storedCondo.id)
                ? storedCondo
                : null;

              if (!currentCondo) {
                currentCondo = (user.condoId && availableCondos.find(c => c.id === user.condoId))
                  || availableCondos[0]
                  || null;
              }
              
              set({
                user,
                isAuthenticated: true,
                availableCondos,
                currentCondo,
                isLoading: false
              });
              
            } catch (apiError) {
              console.warn('API auth check failed:', apiError);
              clearTokens();
              set({
                user: null,
                isAuthenticated: false,
                isLoading: false
              });
            }
          }
        } catch (error) {
          console.error('Auth check error:', error);
          clearTokens();
          set({ 
            user: null, 
            isAuthenticated: false,
            isLoading: false 
          });
        }
      },

      // Update User Profile
      updateUserProfile: async (userData: Partial<User>) => {
        const { user } = get();
        if (!user) return;

        set({ isLoading: true });
        
        try {
          // In development, just update local state
          if (process.env.NODE_ENV === 'development') {
            await new Promise(resolve => setTimeout(resolve, 500));
            set({
              user: { ...user, ...userData },
              isLoading: false
            });
          } else {
            // In production, update via API
            const updatedUser = await apiService.updateUserProfile(userData);
            set({
              user: updatedUser,
              isLoading: false
            });
          }
        } catch (error: any) {
          set({ 
            error: error.message || 'Failed to update profile',
            isLoading: false
          });
          throw error;
        }
      },

      // Sprint 123.2 — owner profile SELF-edit. Unlike updateUserProfile (which
      // dev-mocks and targets a legacy /users/profile path), this always calls
      // the live, self-scoped PUT /api/auth/user/{id} with the CURRENT user's id
      // — the only id this UI can produce — so an owner can never edit anyone
      // else. The backend independently enforces self-scope.
      updateMyProfile: async (fields) => {
        const { user } = get();
        if (!user) return;
        set({ isLoading: true });
        try {
          const updated = await apiService.updateMyProfile(user.id, fields);
          // The endpoint returns a UserResponse (id/email/name/phoneNumber/…).
          // Merge onto the existing user so unrelated client-only fields persist.
          set({
            user: {
              ...user,
              name: (updated as any).name ?? user.name,
              email: (updated as any).email ?? user.email,
              phone: (updated as any).phoneNumber ?? (fields.phoneNumber ?? user.phone),
            },
            isLoading: false,
          });
        } catch (error: any) {
          set({ error: error?.message || 'Failed to update profile', isLoading: false });
          throw error;
        }
      },

      // Switch Condo
      switchCondo: (condoId: string) => {
        const { availableCondos } = get();
        const condo = availableCondos.find(c => c.id === condoId);

        if (condo) {
          set({ currentCondo: condo });
          // R11-9 — remember the selection outside auth-storage (which logout
          // wipes) so the next login restores this condo context.
          try { localStorage.setItem('last-condo-id', condo.id); } catch { /* storage unavailable */ }

          // Update user's condo preference in state if not super-admin
          const { user } = get();
          if (user && user.role !== 'super_admin_condo') {
            set({
              user: {
                ...user,
                condoId: condo.id
              }
            });
          }
        }
      },

      // Fetch Available Condos
      fetchAvailableCondos: async () => {
        const { user } = get();
        if (!user) return;

        set({ isLoading: true });
        
        try {
          let condos: Condo[] = [];
          
          // /condos/available is scoped by the backend per role:
          //   super_admin_condo → all condos
          //   admin            → only their assigned condo
          //   owner            → only their assigned condo
          condos = await apiService.getCondos();
          
          set({ availableCondos: condos, isLoading: false });

          // Set currentCondo: keep existing if still valid, else match condoId, else first
          const { currentCondo, user: latestUser } = get();
          if (condos.length > 0) {
            const stillValid = currentCondo && condos.some(c => c.id === currentCondo.id);
            if (!stillValid) {
              const preferred = (latestUser?.condoId && condos.find(c => c.id === latestUser.condoId))
                || condos[0];
              set({ currentCondo: preferred });
            }
          }
          
        } catch (error: any) {
          set({ 
            error: error.message || 'Failed to fetch condos',
            isLoading: false 
          });
        }
      },

      // Feature Settings Actions
      setFeatureSettings: (settings: FeatureSettings) => {
        set({ featureSettings: settings });
      },
      updateFeatureSetting: (featureKey: string, visibility: Partial<FeatureVisibility>) => {
        const { featureSettings } = get();
        set({
          featureSettings: {
            ...featureSettings,
            [featureKey]: {
              ...(featureSettings[featureKey] || { adminVisible: true, ownerVisible: false, boardMemberVisible: false }),
              ...visibility,
            }
          }
        });
      },
      resetFeatureSettings: () => {
        set({ featureSettings: DEFAULT_FEATURE_SETTINGS });
      },

      // Set Current Condo
      setCurrentCondo: (condo: Condo | null) => {
        set({ currentCondo: condo });
        // R11-9 — see switchCondo: persist so re-login restores the context.
        if (condo) {
          try { localStorage.setItem('last-condo-id', condo.id); } catch { /* storage unavailable */ }
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user ? {
          ...state.user,
          // Never persist sensitive data
          password: undefined
        } : null,
        isAuthenticated: state.isAuthenticated,
        currentCondo: state.currentCondo,
        availableCondos: state.availableCondos,
        featureSettings: state.featureSettings,
      }),
      onRehydrateStorage: () => {
        return (_state, error) => {
          // Sprint 94 (finding 94.5) — removed the unconditional
          // `console.log('Auth store rehydrated successfully')`: it fired on
          // every page load and leaked rehydration state to the console.
          // Only genuine errors are worth surfacing.
          if (error) {
            console.error('Auth store rehydration error:', error);
          }
        };
      },
    }
  )
);

// Optional: Create a simpler hook for common auth checks
export const useAuth = () => {
  const { user, isAuthenticated, isLoading, error } = useAuthStore();
  
  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    isOwner: user?.role === 'owner',
    isAdmin: user?.role === 'admin',
    isSuperAdmin: user?.role === 'super_admin_condo'
  };
};

// Export mock data for testing
export const mockAuthData = {
  users: mockUsers,
  condos: mockCondos
};